import { useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function OrderConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!state?.orderId) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
        <div className="text-center">
          <h2 className="font-heading text-2xl text-[#78350F] mb-2">
            No order found
          </h2>
          <p className="font-body text-stone-500 text-sm mb-6">
            It seems you haven't placed an order yet.
          </p>
          <Button
            data-testid="browse-menu-from-confirmation"
            onClick={() => navigate("/menu")}
            className="rounded-full bg-[#EA580C] hover:bg-[#C2410C] text-white font-body font-semibold px-8"
          >
            Browse Menu
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div
        className="max-w-md w-full text-center animate-fade-up"
        data-testid="order-confirmation"
      >
        {/* Success Icon */}
        <div className="relative mx-auto mb-8">
          <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto animate-scale-in">
            <CheckCircle2 className="h-10 w-10 text-green-600" />
          </div>
          <div className="absolute inset-0 w-20 h-20 mx-auto rounded-full bg-green-100 animate-ping opacity-20" />
        </div>

        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#78350F] mb-3">
          Order Confirmed!
        </h1>
        <p className="font-body text-stone-500 mb-2">
          Thank you{state.customerName ? `, ${state.customerName}` : ""}! Your
          order has been received.
        </p>

        {/* Order Details Card */}
        <div className="bg-white rounded-2xl border border-stone-100 p-6 mt-6 mb-8 shadow-[0_2px_12px_rgb(0,0,0,0.03)]">
          <div className="flex justify-between items-center mb-4">
            <span className="font-body text-xs text-stone-400 uppercase tracking-widest">
              Order ID
            </span>
            <span
              data-testid="order-id-display"
              className="font-body font-bold text-[#EA580C]"
            >
              #{state.orderId.slice(0, 8).toUpperCase()}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="font-body text-xs text-stone-400 uppercase tracking-widest">
              Total
            </span>
            <span
              data-testid="order-total-display"
              className="font-heading text-2xl font-bold text-[#78350F]"
            >
              ${state.subtotal}
            </span>
          </div>
        </div>

        <div className="bg-amber-50 rounded-xl p-4 mb-8 border border-amber-100">
          <p className="font-body text-sm text-stone-600">
            An order confirmation has been sent to the business. They will
            contact you to finalize the details.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/menu">
            <Button
              data-testid="order-more-btn"
              className="rounded-full bg-[#EA580C] hover:bg-[#C2410C] text-white font-body font-semibold px-8 h-11 shadow-lg transition-all active:scale-95"
            >
              Order More
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <a href="tel:6092880605">
            <Button
              data-testid="call-btn-confirmation"
              variant="outline"
              className="rounded-full border-2 border-stone-200 text-stone-600 hover:bg-stone-50 font-body font-semibold px-8 h-11 transition-all"
            >
              <Phone className="mr-2 h-4 w-4" />
              Call Us
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
}
